import React, { useState, useRef } from 'react';
import { 
  ExclamationCircleIcon, 
  CloudArrowUpIcon, 
  StarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const DynamicField = ({ field, value, onChange, error, disabled = false }) => {
  const [fileUploading, setFileUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setFileUploading(true);
    try {
      // For now, we'll store file info and let the parent handle upload
      // In a full implementation, you'd upload to a storage service
      const fileData = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        // In production, upload file and store URL
        url: URL.createObjectURL(file)
      }));
      
      onChange(field.settings?.multiple ? fileData : fileData[0]);
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setFileUploading(false);
    }
  };

  const renderField = () => {
    const baseClassName = `
      block w-full rounded-md border-gray-300 dark:border-gray-600 
      shadow-sm focus:border-purple-500 focus:ring-purple-500 
      dark:bg-gray-700 dark:text-white sm:text-sm
      ${error ? 'border-red-300 dark:border-red-600' : ''}
    `;

    const placeholderText = field.placeholder || `Enter ${field.label.toLowerCase()}`;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholderText}
            disabled={disabled}
            className={baseClassName}
            maxLength={field.validation?.maxLength}
            minLength={field.validation?.minLength}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholderText}
            disabled={disabled}
            rows={field.settings?.rows || 4}
            className={baseClassName.replace('h-10', '')}
            maxLength={field.validation?.maxLength}
            minLength={field.validation?.minLength}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'Enter email address'}
            disabled={disabled}
            className={baseClassName}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'Enter phone number'}
            disabled={disabled}
            className={baseClassName}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholderText}
            disabled={disabled}
            className={baseClassName}
            min={field.validation?.min}
            max={field.validation?.max}
            step={field.validation?.step || 1}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseClassName}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case 'select':
        return (
          <select
            id={field.id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseClassName}
          >
            <option value="">Choose {field.label.toLowerCase()}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.id}-${option.value}`}
                  name={field.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabled}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600"
                />
                <label
                  htmlFor={`${field.id}-${option.value}`}
                  className="ml-2 text-sm text-gray-900 dark:text-gray-100"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'checkbox':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${field.id}-${option.value}`}
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter(v => v !== option.value);
                    onChange(newValues);
                  }}
                  disabled={disabled}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label
                  htmlFor={`${field.id}-${option.value}`}
                  className="ml-2 text-sm text-gray-900 dark:text-gray-100"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'file':
        return (
          <div className="space-y-3">
            <div
              className={`
                border-2 border-dashed rounded-lg p-6 text-center
                ${error ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'}
                ${disabled ? 'bg-gray-50 dark:bg-gray-800' : 'hover:border-purple-400 dark:hover:border-purple-500'}
              `}
            >
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-2">
                <label
                  htmlFor={field.id}
                  className={`
                    cursor-pointer text-sm font-medium text-purple-600 dark:text-purple-400
                    ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:text-purple-500'}
                  `}
                >
                  Choose {field.settings?.multiple ? 'files' : 'file'}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id={field.id}
                  onChange={handleFileChange}
                  disabled={disabled || fileUploading}
                  multiple={field.settings?.multiple}
                  accept={field.validation?.accept}
                  className="sr-only"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {field.validation?.accept || 'Any file type'}
                {field.validation?.maxSize && ` • Max ${field.validation.maxSize}`}
              </p>
            </div>
            
            {/* File Preview */}
            {value && (
              <div className="space-y-2">
                {Array.isArray(value) ? value.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = value.filter((_, i) => i !== index);
                        onChange(newFiles.length > 0 ? newFiles : null);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )) : (
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{value.name}</span>
                    <button
                      type="button"
                      onClick={() => onChange(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'rating':
        const maxRating = field.settings?.max || 5;
        const currentRating = parseInt(value) || 0;
        
        return (
          <div className="flex items-center space-x-1">
            {[...Array(maxRating)].map((_, index) => {
              const ratingValue = index + 1;
              const Icon = ratingValue <= currentRating ? StarIconSolid : StarIcon;
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onChange(ratingValue)}
                  disabled={disabled}
                  className={`
                    p-1 rounded focus:outline-none focus:ring-2 focus:ring-purple-500
                    ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:scale-110'}
                  `}
                >
                  <Icon 
                    className={`
                      h-6 w-6 transition-colors
                      ${ratingValue <= currentRating 
                        ? 'text-yellow-400' 
                        : 'text-gray-300 dark:text-gray-600'
                      }
                    `}
                  />
                </button>
              );
            })}
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              ({currentRating} of {maxRating})
            </span>
          </div>
        );

      case 'slider':
        const min = field.validation?.min || 0;
        const max = field.validation?.max || 100;
        const step = field.validation?.step || 1;
        const currentValue = parseFloat(value) || min;
        
        return (
          <div className="space-y-2">
            <input
              type="range"
              id={field.id}
              min={min}
              max={max}
              step={step}
              value={currentValue}
              onChange={(e) => onChange(parseFloat(e.target.value))}
              disabled={disabled}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>{min}</span>
              <span className="font-medium text-purple-600 dark:text-purple-400">
                {currentValue}
              </span>
              <span>{max}</span>
            </div>
          </div>
        );

      case 'signature':
        return (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Signature pad would be implemented here
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Click to sign or upload signature image
              </p>
            </div>
            {value && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                ✓ Signature captured
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-red-500 text-sm p-2 border border-red-300 rounded">
            Unsupported field type: {field.type}
          </div>
        );
    }
  };

  return (
    <div className="dynamic-field">
      <div className="flex items-center justify-between mb-2">
        <label
          htmlFor={field.id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {field.label}
          {field.required && (
            <span className="text-red-500 ml-1" aria-label="Required">*</span>
          )}
        </label>
        
        {field.settings?.info && (
          <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs text-right">
            {field.settings.info}
          </div>
        )}
      </div>

      {renderField()}

      {error && (
        <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          {error}
        </div>
      )}

      {field.settings?.helpText && !error && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {field.settings.helpText}
        </div>
      )}
    </div>
  );
};

export default DynamicField;